/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  VStack,
  Divider,
  Avatar,
  Box,
  Heading,
  Image,
  Spinner,
  CloseButton,
  Button,
  useToast,
  HStack,
} from '@chakra-ui/react';
import { useTheme } from '../../../contexts/ThemeProvider';
import { Text } from '../../../components/atoms/Text';
import React, { useEffect } from 'react';
import { generatePromotionCode } from '@/service/club/api';
import { IPromotionCard, IPromotionCode } from '@/service/club/types';
import { format } from 'date-fns';
import { FaCopy } from 'react-icons/fa6';
import { useProfile } from '@/contexts/ProfileProvider';
import { masks } from '@/utils/masks';
import { spawnAppEvent } from '@/service/utils';
import { APP_EVENTS } from '@/service/utils';

export function VirtualModal({
  isOpen,
  mode,
  onClose,
  discount,
  modalExperienceRef,
  discountId,
  link,
  code,
}: {
  mode?: string;
  discountId?: number;
  modalExperienceRef: any;
  discount?: IPromotionCard;
  isOpen: boolean;
  link?: string;
  code?: string;
  onClose: () => void;
}) {
  const { colors, logo, isApp, isProductSuperApp, product } = useTheme();
  const { profile } = useProfile();
  const [isLoading, setIsLoading] = React.useState(false);
  const [redirected, setRedirected] = React.useState<string>();
  const [promotionCode, setPromotionCode] = React.useState<IPromotionCode>();

  const toast = useToast();

  function howToUse() {
    if (mode === 'local') {
      return 'Mostre o cartao virtual usando o botão abaixo e apresente-o ao estabelecimento.';
    }

    if (code) {
      return 'Acesse o site usando o link abaixo e use o cupom no fim da compra.';
    }

    return 'Acesse o site usando o link premiado abaixo, seu desconto será aplicado automaticamente.';
  }

  const getProductId = (): number | undefined => {
    if (isProductSuperApp && product) {
      return product.id;
    }
    return undefined;
  };

  const getCode = async () => {
    try {
      setIsLoading(true);

      if (!discountId) return undefined;

      const productId = getProductId();
      const { data } = await generatePromotionCode(discountId.toString(), productId);

      setPromotionCode(data);
      return data;
    } catch {
      toast({
        title: 'Não foi possível gerar o cupom.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return undefined;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    if (mode === 'local') {
      getCode();
    }
  }, [isOpen, code, mode]);

  useEffect(() => {
    if (isOpen) {
      spawnAppEvent(APP_EVENTS.TOGGLE_SCREENSHOT, false);

      return () => spawnAppEvent(APP_EVENTS.TOGGLE_SCREENSHOT, true);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && link && redirected === 'not') {
      setRedirected('pending');

      setTimeout(() => {
        setRedirected('done');

        if (isApp) {
          spawnAppEvent(APP_EVENTS.OPEN_LINK, link);
          modalExperienceRef?.current?.open(promotionCode?.code);
          onClose();
          return;
        } else {
          window.open(link, '_blank');
        }

        const interval = setInterval(() => {
          if (!document.hasFocus() || isApp) {
            modalExperienceRef?.current?.open(promotionCode?.code);
            onClose();
            clearInterval(interval);
          }
        }, 1000);
      }, 2000);
    }
  }, [link, discount, isOpen, redirected, isApp, modalExperienceRef, onClose, promotionCode?.code]);

  return (
    <Modal
      onClose={() => {
        onClose();
        if (promotionCode?.code) {
          modalExperienceRef?.current?.open(promotionCode?.code);
        }
      }}
      isCentered
      id="virtual-card-modal"
      isOpen={isOpen}
    >
      <ModalOverlay />
      <ModalContent margin={'20px'} borderRadius={'16px'}>
        <ModalBody padding="20px">
          <VStack spacing={7} padding="20px">
            <VStack width="100%" spacing={0}>
              <CloseButton
                onClick={() => {
                  onClose();
                  if (promotionCode?.code) {
                    modalExperienceRef?.current?.open(promotionCode?.code);
                  }
                }}
                padding={0}
                backgroundColor={'transparent'}
                marginLeft={'auto'}
                fontWeight={'bold'}
                fontSize={20}
                color={colors.primaryColor}
              />
              <Box
                backgroundColor="#747474"
                width="80px"
                height={'11px'}
                borderRadius={'7px'}
                boxShadow={'0px 4px 4px 0px #00000040 inset'}
              />
            </VStack>

            {promotionCode?.code && (
              <VStack spacing={2}>
                <Avatar width={165} height={165} src={profile?.image} />
                <Heading textAlign={'center'} lineHeight={'30px'} fontFamily={'Glory'} fontWeight="700" fontSize={33}>
                  {profile?.fullName}
                </Heading>
                <Text color="#A7A7A7" fontWeight={'500'} fontSize={14}>
                  CPF: {profile?.cpf && masks.cpf(profile?.cpf)}
                </Text>
                <Divider />
              </VStack>
            )}
            <Image src={logo} objectFit={'contain'} height={50} />
            <VStack spacing={4}>
              <Text textAlign={'center'} color="#747474" fontSize={14} fontWeight={400} fontFamily={'Glory'}>
                {howToUse()}
              </Text>

              <Box
                onClick={() => {
                  const codeCopy = code || promotionCode?.code;
                  if (codeCopy) {
                    navigator.clipboard.writeText(codeCopy).then(() => {
                      toast({
                        title: 'Código copiado!',
                        description: 'O código foi copiado para a área de transferência.',
                        status: 'success',
                        duration: 2000,
                        isClosable: true,
                      });
                    });
                  }
                }}
                bg={colors.primaryColor}
                width={'100%'}
                borderRadius={12}
              >
                {isLoading ? (
                  <Box textAlign={'center'} padding={'10px'}>
                    <Spinner padding={'5px'} color={'white'} size="md" />
                  </Box>
                ) : (
                  <>
                    {(promotionCode?.code || code) && (
                      <HStack align={'center'} justify={'center'} gap={2}>
                        <Text
                          fontFamily={'Glory'}
                          color="white"
                          lineHeight={'43px'}
                          letterSpacing={'-0.045em'}
                          fontSize={36}
                          padding={'5px'}
                          textAlign={'center'}
                          fontWeight={'bold'}
                        >
                          {code || promotionCode?.code}
                        </Text>
                        <FaCopy color={'white'} size={25} />
                      </HStack>
                    )}
                  </>
                )}
              </Box>
              {link && mode !== 'local' && (
                <Button
                  onClick={async () => {
                    const generated = await getCode();
                    if (!generated?.code) {
                      return;
                    }
                    setRedirected('not');
                  }}
                  variant={'outline'}
                  isLoading={isLoading || redirected === 'pending'}
                  isDisabled={redirected === 'done'}
                  borderColor={colors.primaryColor}
                  borderWidth={2}
                  borderRadius={25}
                  fontSize={14}
                  padding={4}
                  color={colors.primaryColor}
                  width={'100%'}
                >
                  Clique aqui para acessar o site
                </Button>
              )}
              {promotionCode?.code && (
                <Text textAlign={'center'} color="#747474" fontSize={14} fontWeight={400} fontFamily={'Glory'}>
                  Cartão gerado em {format(new Date(promotionCode?.created_at), 'dd/MM/yyyy HH:mm')}
                </Text>
              )}
            </VStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
